// SCREENS-S1: the Screens tab's reads (the overview, the logins) and their keys.
// SCREENS-VIEWER: one lease out of the overview, the sidebar's groups, and the fast poll while a
// screen is starting or waiting.
import type { OperationOutput } from "@companyos/sdk";
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export type ScreensOverview = OperationOutput<"screens.overview">;
export type Lease = ScreensOverview["live"][number];
export type Stack = NonNullable<ScreensOverview["stack"]>;
export type OperatorScreen = ScreensOverview["operator"][number];
export type BrowserAccount = OperationOutput<"browserAccounts.list">["data"][number];

export const screensKeys = {
  all: (co: string) => ["company", co, "screens"] as const,
  overview: (co: string) => ["company", co, "screens", "overview"] as const,
  accounts: (co: string) => ["company", co, "screens", "accounts"] as const,
};

/** Every 5 s; every 1.5 s while a lease waits in the queue (it may start any moment). */
export const OVERVIEW_POLL_MS = 5_000;
export const OVERVIEW_FAST_POLL_MS = 1_500;

export const screensOverviewQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: screensKeys.overview(co),
    queryFn: () => api.client.screens.overview({ params: { co } }),
    refetchInterval: (q) =>
      q.state.data?.waiting.length ? OVERVIEW_FAST_POLL_MS : OVERVIEW_POLL_MS,
  });

export const browserAccountsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: screensKeys.accounts(co),
    queryFn: () => api.client.browserAccounts.list({ params: { co } }),
  });

/** A lease of the overview by id (live or waiting); null when it is not there (ended). */
export function findLease(o: ScreensOverview, id: string): Lease | null {
  return o.live.find((l) => l.id === id) ?? o.waiting.find((l) => l.id === id) ?? null;
}

export type SessionGroups = { needsYou: Lease[]; live: Lease[]; waiting: Lease[] };

/** The sidebar's groups: Needs you (mine to help), Live (desktops and browsers), Waiting. */
export function groupSessions(o: ScreensOverview): SessionGroups {
  const needs = new Set(o.needs_you.map((l) => l.id));
  return {
    needsYou: o.needs_you,
    live: o.live.filter((l) => !needs.has(l.id)),
    waiting: o.waiting,
  };
}

/** I drive it: I took it over, or it is my own screen (a person's lease is theirs to drive). */
export function drivenBy(l: Lease, me: string | null): boolean {
  if (!me) return false;
  if (l.controller?.membership_id === me) return true;
  return l.holder.kind === "person" && l.holder.membership_id === me && !l.controller;
}

/**
 * The relay's WebSocket URL from a ticket's `url` (absolute or a `/v1/…` path on the API):
 * resolved against the API root the app uses (same origin when it is `/`), http → ws.
 */
export function relaySocketUrl(ticketUrl: string, apiBase: string | undefined): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
  const root = new URL(apiBase || "/", origin).toString().replace(/\/+$/, "");
  const u = new URL(/^[a-z]+:\/\//i.test(ticketUrl) ? ticketUrl : `${root}${ticketUrl}`);
  u.protocol = u.protocol === "https:" ? "wss:" : u.protocol === "http:" ? "ws:" : u.protocol;
  return u.toString();
}
